import { audioContextIsReadyToUse, useRefAutoUpdate, useTriggerable } from "@kalyzee/kast-web-sdk/web-components";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import { ErrorView } from "../../../components/utils/ErrorView";
import { LoadingView } from "../../../components/utils/LoadingView";
import { toastError } from "../../../helpers/toast";
import { useDynamicWebcam, useMeetingConfigurator, useMeetingSelector, useMultiMeetingAPI } from "../../../hooks/meeting";
import { useSettings } from "../../../hooks/settings";

import { Button3D, Icons } from "@kalyzee/escaet-ui";
import { MeetingContainerAPI, MeetingSessionOvenMediaReceiver, useMeetingAudioMixer } from "@kalyzee/kast-web-sdk";
import { GoTo } from "../../../components/navigation/GoTo";
import { PageMasterMode, usePageContextRef } from "../../../components/navigation/PageContext";
import { DevSoundIndicatorsBanner } from "../../../components/utils/DevSoundIndicatorBanner";
import { getAudioContextForAudioMixer, getAudioContextForVolumeAnalyzer } from "../../../helpers/audio";
import { joinMeetingFromPedagogy } from "../../../helpers/meeting";
import { DEFAULT_VIDEO_PARAMS } from "../../../helpers/videos";
import { pedagogyWebcamName } from "../../../helpers/webcam";
import { usePedagogyAvailableViews, usePopulatedMeetingData } from "../../../hooks/populatedData";
import { ConfirmableEscaetPedagogyAction } from "../../../interfaces/pedagogy";
import { PedagogyView } from "../../../interfaces/populatedData";
import { useUser } from "../../../store/session/hooks";
import { usePedagogyActions } from "./actions";
import styles from "./container.module.css";
import { usePedagogyContext } from "./context";
import { mergeDeep } from "../../../helpers/object";

// TODO-V4 : load from backend
const PedaMeetings = ["64b02c87c020a10012c3be06"];

const WEBCAM_VIEWS: PedagogyView["type"][] = ["communication"];

export const PedagogyPageContainer = () => {
  const [settings] = useSettings();
  const settingsRef = useRefAutoUpdate(settings);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(false);
  const multiMeetingAPI = useMultiMeetingAPI();
  const multiMeetingAPIRef = useRefAutoUpdate(multiMeetingAPI);
  const [interactionRequired, setInteractionRequired] = useState(false);
  const pedagogyCtx = usePedagogyContext();
  const pedagogyCtxRef = useRefAutoUpdate(pedagogyCtx);
  const pageCtxRef = usePageContextRef();

  const padagogyMeetingContainers = useMemo(() => {
    const containers = multiMeetingAPI.all?.getContainers()?.filter((c) => PedaMeetings.includes(c.getMeetingId()));
    return containers ?? [];
  }, [multiMeetingAPI]);

  const selfUser = useUser();

  // Multi Peda Meeting not handled
  const pedagogyMeetingAPI = useMemo(() => padagogyMeetingContainers[0]?.getAPI(), [padagogyMeetingContainers]);
  const [pedagogyMeetingId] = useMeetingSelector(pedagogyMeetingAPI, (state) => state.meeting?.id);
  const [emissionSessions] = useMeetingSelector(pedagogyMeetingAPI, (state) => state.emissionSessions);

  const populatedData = usePopulatedMeetingData(pedagogyMeetingAPI);
  const views = usePedagogyAvailableViews(populatedData, selfUser?.id);
  const currentView = views[0];

  const logoContainerRef = useRef<HTMLImageElement>(null);
  useTriggerable(logoContainerRef, { clickCount: 3 }, () => pageCtxRef.current?.openSettingsPopup()); // press click 3 times with mouse(left button) on logo

  useMeetingConfigurator(pedagogyMeetingAPI, {
    ovenMediaSession: {
      emission: {
        options: (mediaSession) => {
          if (mediaSession.extra?.type === "camera") {
            return {
              rtc: mergeDeep(
                {
                  streamParams: {
                    video: {
                      rtcParams: {
                        degradationPreference: "maintain-framerate",
                      },
                      rtcEncoding: {
                        maxBitrate: DEFAULT_VIDEO_PARAMS.pedagogy.webcam.video.maxBitrate,
                        maxFramerate: DEFAULT_VIDEO_PARAMS.pedagogy.webcam.video.maxFramerate,
                      },
                    },
                    audio: { mediaContentHint: "speech", rtcEncoding: { maxBitrate: DEFAULT_VIDEO_PARAMS.pedagogy.webcam.audio.maxBitrate } },
                  },
                },
                settingsRef.current?.debug?.rtc?.pedagoy?.webcam
              ),
            };
          }
          if (mediaSession.extra?.type === "screen" || mediaSession.extra?.type === "board") {
            return {
              rtc: mergeDeep(
                {
                  streamParams: {
                    video: {
                      mediaContentHint: "detail",
                      rtcParams: {
                        degradationPreference: "maintain-resolution",
                      },
                      rtcEncoding: {
                        maxBitrate: DEFAULT_VIDEO_PARAMS.pedagogy.screensharing.video.maxBitrate,
                        maxFramerate: DEFAULT_VIDEO_PARAMS.pedagogy.screensharing.video.maxFramerate,
                      },
                    },
                    audio: { rtcEncoding: { maxBitrate: DEFAULT_VIDEO_PARAMS.pedagogy.screensharing.audio.maxBitrate } },
                  },
                },
                settingsRef.current?.debug?.rtc?.pedagoy?.screensharing
              ),
            };
          }
          return undefined;
        },
      },
      reception: {
        options: () => ({
          audio: false,
          video: { mode: "subscription", delayToDisableStream: 5000 },
        }),
      },
    },
  });

  const actions = usePedagogyActions(
    pedagogyMeetingId,
    currentView?.group?.room,
    selfUser?.id,
    useCallback((action: ConfirmableEscaetPedagogyAction) => {
      pedagogyCtxRef.current?.setConfirmableAction(action);
    }, [])
  );

  useEffect(() => {
    pedagogyCtxRef.current?.setActions(actions);
  }, [actions]);

  // Webcam sharing API
  const webcam = pedagogyCtx?.webcam;
  const enableWebcam = useMemo(() => currentView?.type && WEBCAM_VIEWS.includes(currentView.type), [currentView]);
  useDynamicWebcam(pedagogyMeetingAPI, pedagogyWebcamName, enableWebcam ? webcam?.mediaStream : undefined);
  // Webcam sharing API

  // ---------------------------------------------------------------------- //
  // ---------------------------   UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  const joinAll = async () => {
    // get meeting from backend
    const meetingsAPI = multiMeetingAPIRef.current.all;
    if (!meetingsAPI) {
      toastError("Une erreur est apparue pendant le chargement des données...");
      setError(true);
      return;
    }
    const result = await Promise.all(PedaMeetings.map((id) => joinMeetingFromPedagogy(meetingsAPI, id, pedagogyCtxRef.current)));
    const success = result.reduce((p, c) => (c ? true : false) && p, true);
    if (!success) {
      toastError("Une erreur est apparue pendant le chargement des données...");
      setError(true);
      return;
    }
    setReady(true);
  };

  const leaveAll = async () => {
    await Promise.allSettled(
      PedaMeetings.map(async (id) => {
        const container = multiMeetingAPIRef.current.all?.getContainers().find((c) => c.getMeetingId() === id);
        if (container) await container.getAPI().leave();
      })
    );
  };

  const checkAudioContext = () => {
    audioContextIsReadyToUse(getAudioContextForVolumeAnalyzer()).then((ready) => {
      setInteractionRequired(!ready);
      if (ready) joinAll();
    });
  };

  const renderOutletOrRedirect = () => {
    const currentPage = pageCtxRef.current?.page;

    const allowedPages: PageMasterMode[] = [];
    if (currentView?.type === "communication") {
      allowedPages.push(PageMasterMode.WAITING_ROOM);
    } else {
      allowedPages.push(PageMasterMode.DASHBOARD);
      allowedPages.push(PageMasterMode.OVERVIEW);
      allowedPages.push(PageMasterMode.MEETING_DASHBOARD);
      allowedPages.push(PageMasterMode.MEETING_INTERVENTION);
      allowedPages.push(PageMasterMode.MEETING_INTERVENTION_DASHBOARD);
      allowedPages.push(PageMasterMode.MEETING_INTERVENTION_GROUP);
      allowedPages.push(PageMasterMode.MEETING_INTERVENTION_SURVEY);
      allowedPages.push(PageMasterMode.MEETING_INTERVENTION_HIGHLIGHT);
      allowedPages.push(PageMasterMode.MEETING_INTERVENTION_TALK_TO_EXPERT);
    }

    if (currentPage && currentPage !== PageMasterMode.MEETING && !allowedPages.includes(currentPage)) {
      const previousPage = pageCtxRef.current?.getPreviousPage();
      if (allowedPages.includes(previousPage)) {
        // go to previous page if allowed
        pageCtxRef.current?.goToPrevious(allowedPages[0], pageCtxRef.current?.params);
        return <LoadingView />;
      }
      return <GoTo page={allowedPages[0]} params={pageCtxRef.current?.params} />;
    } else {
      return <Outlet />;
    }
  };

  // ---------------------------------------------------------------------- //
  // ---------------------------   EFFECT ----------------------------- //
  // ---------------------------------------------------------------------- //

  const audioMixer = useMeetingAudioMixer(
    getAudioContextForAudioMixer(),
    pedagogyMeetingAPI,
    true,
    useCallback(
      (_: MeetingContainerAPI, session: MeetingSessionOvenMediaReceiver) => {
        return pedagogyCtx?.audioValidator(session) ?? false;
      },
      [pedagogyCtx]
    )
  );
  if (settings.debug?.audio?.mixer) (window as any).audioMixerPeda = audioMixer;

  useEffect(() => {
    emissionSessions?.forEach((s) => {
      const type = s.data.mediaSession.extra?.type;
      let group = "undefined";
      if (type === "camera") group = "cameras";
      else if (type === "screen") group = "screens";
      else if (type === "board") group = "boards";
      if (group) s.data.groups = [group];
    });
  }, [emissionSessions]);

  useEffect(() => {
    checkAudioContext();
    return () => {
      leaveAll();
    };
  }, []);

  useEffect(() => {
    pedagogyCtxRef.current?.setContainers(multiMeetingAPI.all?.getContainers() ?? []);
    pedagogyCtxRef.current?.setPedagogyContainers(padagogyMeetingContainers);
  }, [padagogyMeetingContainers, multiMeetingAPI]);

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER ----------------------------- //
  // ---------------------------------------------------------------------- //

  if (interactionRequired) {
    return (
      <div className={styles.interactionRequiredContainer}>
        <div className={styles.interactionRequiredLogo} ref={logoContainerRef}>
          <img src={Icons.PedagoLogoSamii} alt="logo" />
        </div>
        <div className={styles.interactionRequiredTitle}>Bienvenue à la pédagogie</div>
        <Button3D
          className={styles.interactionRequiredButton}
          onClick={() => {
            checkAudioContext();
          }}
          style={{ minWidth: "200px" }}
        >
          Entrer
        </Button3D>
      </div>
    );
  }

  if (error)
    return (
      <ErrorView
        retry={() => {
          joinAll();
        }}
      />
    );
  if (!ready) return <LoadingView />;

  return (
    <>
      {renderOutletOrRedirect()}
      <DevSoundIndicatorsBanner label={"Pédagogie meeting"} meetingData={populatedData} canHearSession={pedagogyCtx?.audioValidator} />
    </>
  );
};
